import React, { useEffect, useState, useId, useRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  Table,
  VisibilityState,
} from '@tanstack/react-table';
import {
  Table as TableComponent,
  TableCell,
  TableContainer,
  TableHeader,
  ClickableRow,
  FilterBar,
  FilterBarRow,
  SearchInput,
  FilterBarRowSection,
  TableTabList,
  ResultCountContainer,
  ResultCount,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
} from '../Table';
import { TableMultiselectFilter } from '../TableDropdownFilter/TableDropdownFilter';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import {
  getRxFilePreviewUrl,
  getRxFileDownloadUrl,
  OverallStatusFilter,
  RxTableResponseRow,
  StatusInfo,
  DrugCategory,
} from '../../api';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'design-system/src/components/Pagination/Pagination';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PaginationTableProps } from 'design-system/src/components/Pagination/Pagination';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';
import { PortalTableFilterOptions } from '../useFetchPortalTableFilters';
import { RxFilterValues } from '../../types';
import {
  getAddressString,
  getDropdownInitialValueFromSearchParams,
  getDropdownInitialValueFromSearchParamsAndCookie,
  updateTableColumnFilterValueCookie,
  formatStandardDateLocaleString,
  formatStandardDate,
} from '../../utils';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import EyeIcon from 'design-system/src/assets/icons/eye.svg?react';
import styled, { css } from 'styled-components';
import FilePreviewModal from 'design-system/src/components/FilePreviewModal/FilePreviewModal';
import { IconButton } from 'design-system/src/components/Button/Button';
import DownloadTray from 'design-system/src/assets/icons/downloadTray.svg?react';
import Paperclip from 'design-system/src/assets/icons/paperclip.svg?react';
import Checkmark from 'design-system/src/assets/icons/checkmark.svg?react';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import { DRUG_CATEGORY_DROPDOWN_OPTIONS } from '../../constants';
import { TableNullState, TableNullStateType } from '../TableNullState/TableNullState';
import FormattedCell from '../FormattedCell';
import { AuthContext } from '../../auth';

import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from '../SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: var(--border-gray);
  margin-left: 8px;
`;

const MergedRowItem = styled.td`
  text-align: center;
  padding: 2rem;
`;

const EnrollmentCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-wrap: nowrap;
`;

const statusContainerStyles = css`
  color: var(--black);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const tableLinkStyles = css`
  text-decoration: none;
  border-bottom: 1px dashed var(--black);
  width: fit-content; // stops the underline from extending to the end of the cell
`;

const PreviewFileContainer = styled.a`
  ${statusContainerStyles}
  ${tableLinkStyles}

  // We don't display the preview on mobile because users don't see the native pdf frame
  // with the download button on ios and can't scroll between pages
  @media (max-width: ${breakpoints.tabletMax}px) {
    display: none;
  }
`;

const DownloadFileContainer = styled.a`
  ${statusContainerStyles}
  ${tableLinkStyles}

  // We display the download button instead of a preview button on mobile because users
  // don't see the native pdf frame with the download button on ios
  @media (min-width: ${breakpoints.smallDesktopMin}px) {
    display: none;
  }
`;

const columnHelper = createColumnHelper<RxTableResponseRow>();

type StatusTabValue = 'open' | 'awaiting_office' | 'awaiting_patient' | 'completed' | 'all';

const LoadingComponent = ({ table }: { table: Table<RxTableResponseRow> }) => {
  return (
    <tr>
      <MergedRowItem colSpan={table.getAllColumns().length}>
        <LoadingSpinner height="200px" />
      </MergedRowItem>
    </tr>
  );
};

const getNullStateType = (initialFilterValues?: RxFilterValues, searchValue?: string) => {
  // Check if there's a search value applied
  if (searchValue && searchValue !== '') {
    return TableNullStateType.NO_RESULTS_FROM_FILTERS;
  }

  if (
    !!initialFilterValues?.office ||
    !!initialFilterValues?.prescriber ||
    !!initialFilterValues?.drug ||
    !!initialFilterValues?.drug_category
  ) {
    return TableNullStateType.NO_RESULTS_FROM_FILTERS;
  }
  if (initialFilterValues?.awaitingOffice) {
    return TableNullStateType.NO_PRESCRIPTIONS_AWAITING_OFFICE;
  }
  if (initialFilterValues?.awaitingPatient) {
    return TableNullStateType.NO_PRESCRIPTIONS_AWAITING_PATIENT;
  }
  if (initialFilterValues?.overallStatus === OverallStatusFilter.Completed) {
    return TableNullStateType.NO_PRESCRIPTIONS_COMPLETED;
  }
  return TableNullStateType.NO_PRESCRIPTIONS_OPEN;
};

interface RxTableProps {
  data: Array<RxTableResponseRow>;
  loading: boolean;
  tableProps: PaginationTableProps & {
    submitSearchValue: (search: string) => void;
    filters: PortalTableFilterOptions;
    setPrescriberFilter: (prescriberNamesList: string[]) => void;
    setOfficeFilter: (officeNamesList: string[]) => void;
    setDrugNameFilter: (drugNamesList: string[]) => void;
    setDrugCategoryFilter: (drugCategoriesList: DrugCategory[]) => void;
    setStatusTab: (status: StatusTabValue) => void;
  };
  initialSearchValue?: string;
  initialFilterValues?: RxFilterValues;
  handleDownload: () => void;
  downloadLoading: boolean;
  hasAwaitingOffice?: boolean;
}

const SentToPharmacy = styled.div`
  display: inline;
  white-space: nowrap;
`;

const StyledCheckmark = styled(Checkmark)`
  margin-left: 6px;
`;

const StatusCell = ({
  statusInfo,
  rxId,
  fileHeader,
  type,
}: {
  statusInfo: StatusInfo;
  rxId: string;
  fileHeader?: string;
  type?: 'Hub' | 'Bridge' | 'PAP';
}) => {
  if (statusInfo.status === '') {
    return '';
  }

  const status = type ? `${type} ${statusInfo.status.toLowerCase()}` : statusInfo.status;

  if (statusInfo.status === 'Awaiting office') {
    return <NotificationDot>{status}</NotificationDot>;
  } else if (statusInfo.status === 'Sent to pharmacy') {
    return (
      <SentToPharmacy>
        {statusInfo.status}
        <StyledCheckmark color="var(--green)" aria-hidden />
      </SentToPharmacy>
    );
  } else {
    return <StatusDownloadCell status={status} file_id={statusInfo.file_id} rx_id={rxId} file_header={fileHeader} />;
  }
};

const StatusDownloadCell: React.FC<{
  file_id: string | null;
  status: string;
  rx_id: string;
  file_header?: string;
}> = ({ file_id, status, rx_id, file_header }) => {
  const [isPreviewVisible, setPreviewVisible] = useState(false);

  if (!file_id) {
    return status;
  }

  const handleFilePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
  };

  return (
    // The preview and download components are both rendered, but only one is visible at a time
    // using CSS breakpoints.
    <>
      <PreviewFileContainer onClick={handleFilePreview}>
        {status}
        <Tooltip content="Open document" childNotInteractive={true}>
          <EyeIcon title="" />
        </Tooltip>

        {isPreviewVisible && (
          <FilePreviewModal
            fileUrl={getRxFilePreviewUrl(rx_id, file_id)}
            onClose={handleClosePreview}
            fileHeader={file_header}
            downloadUrl={getRxFileDownloadUrl(rx_id, file_id)}
          />
        )}
      </PreviewFileContainer>

      <DownloadFileContainer href={getRxFileDownloadUrl(rx_id, file_id)} download onClick={(e) => e.stopPropagation()}>
        {status}

        <Tooltip content="Download document" childNotInteractive={true}>
          <Paperclip aria-hidden />
        </Tooltip>
      </DownloadFileContainer>
    </>
  );
};

const RxTable: React.FC<RxTableProps> = ({
  data,
  tableProps,
  loading,
  initialSearchValue,
  initialFilterValues,
  handleDownload,
  downloadLoading,
  hasAwaitingOffice,
}) => {
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(loading);
  const [dataState, setDataState] = useState(data);
  const rxStatusPanelId = useId();
  const rxStatusTabId = useId();
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugCategoryFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const authContext = React.useContext(AuthContext);
  const initialPrescriberOptions = getDropdownInitialValueFromSearchParams(
    authContext.user?.default_filters,
    initialFilterValues?.prescriber,
    'providers',
    tableProps.filters.prescribers,
  ) as MultiselectAutocompleteOption[];
  const [prescriberSelections, setPrescriberSelections] = useState<string[]>(
    initialPrescriberOptions.map((o) => o.value),
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const defaultPrescriberNpis = authContext.user?.default_filters?.providers.map((p) => p.npi) || [];
  const showSaveButton = !areStringArraysEqual(prescriberSelections, defaultPrescriberNpis);

  const nextTick = () => new Promise((resolve) => setTimeout(resolve, 0));

  const clearAllFilters = async () => {
    // Clear search input and trigger loading state
    setSearchValue('');
    setPageLoading(true);
    tableProps.submitSearchValue('');

    // Clear TableMultiselectFilter components
    officeFilterRef.current?.clear();
    // We need to pause between clearing each filter
    await nextTick();
    prescriberFilterRef.current?.clear();
    await nextTick();
    drugFilterRef.current?.clear();
    await nextTick();
    drugCategoryFilterRef.current?.clear();
  };

  const handleWindowResize = () => {
    setColumnVisibility({
      prescriber_name: window.innerWidth >= breakpoints.largeDesktopMin,
    });
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const columns = [
    columnHelper.accessor('prescription_recieved_at', {
      header: 'Date',
      size: DATE_COLUMN_SIZE,
      cell: (info) => formatStandardDateLocaleString(info.getValue()),
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, patient_dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            body={`DOB: ${formatStandardDate(info.getValue().patient_dob)}`}
          />
        ),
        size: DEFAULT_COLUMN_SIZE,
      },
    ),
    columnHelper.accessor('drug_name', {
      header: 'Drug',
      cell: (info) => (
        <FormattedCell id={info.row.original.id} title={info.getValue()} body={info.row.original.name} truncateBody />
      ),
    }),
    columnHelper.accessor('pa_appeal_status', {
      header: 'Coverage',
      cell: (info) =>
        info.getValue()?.status ? (
          <StatusCell statusInfo={info.getValue()} rxId={info.row.original.id} fileHeader="PA Letter" />
        ) : (
          emptyTableCellCopy
        ),
      size: DEFAULT_COLUMN_SIZE,
    }),
    columnHelper.accessor(
      (row) => {
        return {
          hubStatus: row.hub_status,
          bridgeStatus: row.bridge_status,
          papStatus: row.pap_status,
        };
      },
      {
        header: 'Enrollment',
        cell: (info) => {
          return info.getValue().hubStatus.status !== '' ||
            info.getValue().bridgeStatus.status !== '' ||
            info.getValue().papStatus.status !== '' ? (
            <EnrollmentCell>
              <StatusCell
                statusInfo={info.getValue().hubStatus}
                rxId={info.row.original.id}
                fileHeader="Hub Form"
                type="Hub"
              />
              <StatusCell
                statusInfo={info.getValue().bridgeStatus}
                rxId={info.row.original.id}
                fileHeader="Bridge Form"
                type="Bridge"
              />
              <StatusCell
                statusInfo={info.getValue().papStatus}
                rxId={info.row.original.id}
                fileHeader="PAP Letter"
                type="PAP"
              />
            </EnrollmentCell>
          ) : (
            emptyTableCellCopy
          );
        },
        size: DEFAULT_COLUMN_SIZE,
      },
    ),
    columnHelper.accessor('pharmacy_status', {
      header: 'Pharmacy',
      cell: (info) => {
        const cellComponent = info.getValue().status ? (
          <StatusCell statusInfo={info.getValue()} rxId={info.row.original.id} />
        ) : (
          <span>{emptyTableCellCopy}</span>
        );
        if (info.getValue().status && info.row.original.pharmacy_address) {
          const addressString = getAddressString(info.row.original.pharmacy_address);
          return (
            <Tooltip content={addressString} childNotInteractive={true}>
              {cellComponent}
            </Tooltip>
          );
        }
        return cellComponent;
      },
      size: DEFAULT_COLUMN_SIZE,
    }),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      size: DEFAULT_COLUMN_SIZE,
    }),
  ];

  const [searchValue, setSearchValue] = useState(initialSearchValue || '');

  // when the data is done loading, we want to reset the loading and data states
  useEffect(() => {
    if (!loading) {
      setSearchLoading(false);
      setPageLoading(false);
      setDataState(data);
    }
  }, [loading, data]);

  const table = useReactTable({
    // this data state ensures that the data doesn't disappear while it's being refreshed
    data: dataState,
    columns,
    state: {
      pagination: {
        pageIndex: tableProps.pageIndex,
        pageSize: tableProps.perPage,
      },
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    pageCount: Math.ceil(tableProps.total / tableProps.perPage),
    manualPagination: true,
    columnResizeMode: 'onChange',
    onColumnVisibilityChange: setColumnVisibility,
  });

  const NoResultsComponent = ({
    table,
    initialFilterValues,
  }: {
    table: Table<RxTableResponseRow>;
    initialFilterValues?: RxFilterValues;
  }) => {
    return (
      <tr>
        <MergedRowItem colSpan={table.getAllColumns().length}>
          <TableNullState
            nullStateType={getNullStateType(initialFilterValues, searchValue)}
            onClearFilters={clearAllFilters}
          />
        </MergedRowItem>
      </tr>
    );
  };

  return (
    <TableContainer>
      <FilterBar>
        <FilterBarRow>
          <TableTabList
            tabs={[
              { label: 'All open', value: 'open' },
              {
                label: hasAwaitingOffice ? (
                  <NotificationDot srOnlyText="there are prescriptions awaiting office review">
                    Awaiting office
                  </NotificationDot>
                ) : (
                  'Awaiting office'
                ),
                value: 'awaiting_office',
              },
              { label: 'Awaiting patient', value: 'awaiting_patient' },
              { label: 'Completed', value: 'completed' },
              { label: 'All prescriptions', value: 'all' },
            ]}
            selectedValue={(() => {
              if (initialFilterValues?.overallStatus === OverallStatusFilter.Completed) return 'completed';
              if (initialFilterValues?.awaitingOffice) return 'awaiting_office';
              if (initialFilterValues?.awaitingPatient) return 'awaiting_patient';
              if (initialFilterValues?.overallStatus === OverallStatusFilter.All) return 'all';
              return 'open';
            })()}
            onSelect={(value) => {
              setPageLoading(true);
              tableProps.setStatusTab(value as StatusTabValue);
            }}
            tabListId={rxStatusTabId}
            tabPanelId={rxStatusPanelId}
            ariaLabel="Filter prescriptions by status"
          />
        </FilterBarRow>

        <FilterBarRow $reorderOnSmall>
          <SearchInput
            label="Search by patient or drug name"
            hideLabel
            onChange={(e) => {
              setSearchLoading(true);
              const newValue = (e.target as HTMLInputElement).value;
              tableProps.submitSearchValue(newValue);
              setSearchValue(newValue);
            }}
            value={searchValue}
            loading={searchLoading && loading} // the searchLoading state will update a render cycle after the loading state
          />
          <TableMultiselectFilter
            ref={officeFilterRef}
            options={tableProps.filters.offices}
            label={'Office'}
            horizontalAlign="right"
            initialValue={
              getDropdownInitialValueFromSearchParamsAndCookie(
                initialFilterValues?.office,
                'rx',
                'office_name',
              ) as MultiselectAutocompleteOption[]
            }
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              setPageLoading(true);
              tableProps.setOfficeFilter(selectedValuesList);
              updateTableColumnFilterValueCookie(value, 'rx', 'office_name');
            }}
          />
          <TableMultiselectFilter
            ref={prescriberFilterRef}
            options={tableProps.filters.prescribers}
            label={'Prescriber'}
            horizontalAlign="right"
            initialValue={
              getDropdownInitialValueFromSearchParams(
                authContext.user?.default_filters,
                initialFilterValues?.prescriber,
                'providers',
                tableProps.filters.prescribers,
              ) as MultiselectAutocompleteOption[]
            }
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              setPageLoading(true);
              setPrescriberSelections(selectedValuesList);
              tableProps.setPrescriberFilter(selectedValuesList);
            }}
          />
          <TableMultiselectFilter
            ref={drugFilterRef}
            options={tableProps.filters.drugs}
            label={'Drug'}
            horizontalAlign="right"
            initialValue={
              getDropdownInitialValueFromSearchParams(
                authContext.user?.default_filters,
                initialFilterValues?.drug,
                'drugs',
              ) as MultiselectAutocompleteOption[]
            }
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              setPageLoading(true);
              tableProps.setDrugNameFilter(selectedValuesList);
            }}
          />
          <TableMultiselectFilter
            ref={drugCategoryFilterRef}
            options={DRUG_CATEGORY_DROPDOWN_OPTIONS}
            label={'Drug category'}
            horizontalAlign="right"
            initialValue={
              getDropdownInitialValueFromSearchParams(
                authContext.user?.default_filters,
                initialFilterValues?.drug_category,
                'drug_category',
                DRUG_CATEGORY_DROPDOWN_OPTIONS,
              ) as MultiselectAutocompleteOption[]
            }
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              setPageLoading(true);
              tableProps.setDrugCategoryFilter(selectedValuesList as DrugCategory[]);
            }}
          />
          <SaveFiltersButton $visible={showSaveButton} onClick={() => setShowSaveModal(true)} />

          <FilterBarRowSection>
            <ResultCountContainer>
              <ResultCount>
                {tableProps.total} {tableProps.total === 1 ? 'result' : 'results'}
              </ResultCount>
              <Divider />
              <IconButton onClick={handleDownload} isLoading={downloadLoading}>
                <DownloadTray title="download" />
              </IconButton>
            </ResultCountContainer>
          </FilterBarRowSection>
        </FilterBarRow>
      </FilterBar>

      <TableComponent role="tabpanel" id={rxStatusPanelId} aria-labelledby={rxStatusTabId}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id} $width={`${header.getSize()}px`}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {pageLoading ? (
            <LoadingComponent table={table} />
          ) : dataState.length === 0 ? (
            <NoResultsComponent table={table} initialFilterValues={initialFilterValues} />
          ) : (
            table.getRowModel().rows.map((row) => (
              <ClickableRow
                key={row.id}
                onClick={() => navigate(`/rxs/${row.original.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/rxs/${row.original.id}`);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} $width={`${cell.column.getSize()}px`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </ClickableRow>
            ))
          )}
        </tbody>
      </TableComponent>
      {tableProps.total > 1 && (
        <Pagination
          table={table}
          goBack={() => {
            setPageLoading(true);
            tableProps.goBack();
          }}
          goForward={() => {
            setPageLoading(true);
            tableProps.goForward();
          }}
        />
      )}
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            {
              label: 'Prescriber',
              displayValues: prescriberSelections.map(
                (npi) => tableProps.filters.prescribers.find((p) => p.value === npi)?.description || npi,
              ),
              saveValues: prescriberSelections,
            },
          ]}
        />
      )}
    </TableContainer>
  );
};

export default RxTable;
